/**
 * Misiones: siempre hay 3 activas, de tipos distintos, que se cumplen dentro de una sola partida.
 * Al cumplir una se ganan monedas (para el vestuario) y se sustituye por otra de un tipo libre,
 * cada vez algo más difícil. Se guarda en el dispositivo; sin almacenamiento, solo en memoria.
 */
const KEY = 'dopamine-runner.missions.v1';
const ACTIVE = 3;

// target(n): objetivo según el nivel n (0, 1, 2…) del tipo de misión.
export const MISSION_TYPES = {
  coins: { text: (t) => `Recoge ${t} monedas`, target: (n) => 30 + 30 * n },
  distance: { text: (t) => `Recorre ${t} m`, target: (n) => 500 + 500 * n },
  logs: { text: (t) => `Salta ${t} troncos`, target: (n) => 4 + 4 * n },
  brambles: { text: (t) => `Pasa bajo ${t} zarzas`, target: (n) => 4 + 4 * n },
  pits: { text: (t) => `Salta ${t} fosos`, target: (n) => 2 + 2 * n },
  powerUps: { text: (t) => `Coge ${t} potenciadores`, target: (n) => 2 + n },
  streak: { text: (t) => `Llega a racha x${t}`, target: (n) => Math.min(5, 2 + n) },
  turns: { text: (t) => `Toma ${t} curvas`, target: (n) => 10 + 8 * n },
};
const ORDER = Object.keys(MISSION_TYPES);

export const reward = (level) => 50 + 25 * level;

/** Contadores de una partida (los rellena Game). */
export function emptyStats() {
  return { coins: 0, distance: 0, logs: 0, brambles: 0, pits: 0, powerUps: 0, streak: 1, turns: 0 };
}

export class Missions {
  constructor(storage = globalThis.localStorage) {
    this.storage = storage;
    const saved = this.#load();
    this.levels = saved.levels ?? {};
    this.active = (saved.active ?? []).filter((t) => MISSION_TYPES[t]);
    this.earned = saved.earned ?? 0; // monedas ganadas con misiones
    while (this.active.length < ACTIVE) this.active.push(this.#nextType());
    this.startRun();
  }

  /** Lista para la interfaz: [{ type, text, target, reward, progress? }] */
  list(stats) {
    return this.active.map((type) => {
      const level = this.levels[type] ?? 0;
      const target = MISSION_TYPES[type].target(level);
      return { type, text: MISSION_TYPES[type].text(target), target, reward: reward(level), progress: stats ? Math.min(target, Math.floor(stats[type])) : 0 };
    });
  }

  startRun() {
    this.completedThisRun = [];
    this.fresh = new Set(); // misiones nuevas a mitad de partida: cuentan desde la siguiente
  }

  /** Comprueba las misiones activas; devuelve las cumplidas ahora (y las sustituye). */
  check(stats) {
    const done = [];
    for (let i = 0; i < this.active.length; i++) {
      const type = this.active[i];
      if (this.fresh.has(type)) continue;
      const level = this.levels[type] ?? 0;
      const target = MISSION_TYPES[type].target(level);
      if (stats[type] < target) continue;
      const prize = reward(level);
      done.push({ type, text: MISSION_TYPES[type].text(target), reward: prize });
      this.earned += prize;
      this.levels[type] = level + 1;
      this.active.splice(i, 1);
      const next = this.#nextType(type);
      this.active.splice(i, 0, next);
      this.fresh.add(next);
    }
    if (done.length) {
      this.completedThisRun.push(...done);
      this.#save();
    }
    return done;
  }

  /** El siguiente tipo libre, rotando por la lista (el de menor nivel primero). */
  #nextType(exclude) {
    const free = ORDER.filter((t) => !this.active.includes(t) && t !== exclude);
    free.sort((a, b) => (this.levels[a] ?? 0) - (this.levels[b] ?? 0) || ORDER.indexOf(a) - ORDER.indexOf(b));
    return free[0];
  }

  #load() {
    try {
      return JSON.parse(this.storage?.getItem(KEY) ?? '{}') ?? {};
    } catch {
      return {};
    }
  }

  #save() {
    try {
      this.storage?.setItem(KEY, JSON.stringify({ levels: this.levels, active: this.active, earned: this.earned }));
    } catch {}
  }
}
